/**
 * Unified Builder Save Hook
 * 
 * Provides centralized save functionality for all builder tabs.
 * All tabs should use this hook instead of direct save calls.
 */

import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { saveChatbot, saveChatbotSettings, saveChatbotBasicInfo } from '@/services/chatbotSaveService';
import { upsertTheme } from '@/services/chatbotMutationsService';
import { UnifiedChatbotData } from '@/types/builder';
import { ChatTheme } from '@/types/theme';

export interface BuilderSaveState {
  isSaving: boolean;
  lastSaved: Date | null;
  error: Error | null;
}

export interface UseBuilderSaveOptions {
  chatbotId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for saving builder changes
 * Provides unified save functionality for all tabs
 */
export function useBuilderSave({ chatbotId, onSuccess, onError }: UseBuilderSaveOptions) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [state, setState] = useState<BuilderSaveState>({
    isSaving: false,
    lastSaved: null,
    error: null
  });

  /**
   * Save chatbot data (main function)
   */
  const saveChatbotData = useCallback(async (
    updates: Partial<UnifiedChatbotData>,
    options?: { mergeSettings?: boolean; preserveAiSettings?: boolean }
  ) => {
    if (!chatbotId) {
      const error = new Error('Chatbot ID is required');
      setState(prev => ({ ...prev, error }));
      if (onError) onError(error);
      return { success: false, error };
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      const result = await saveChatbot(chatbotId, updates, {
        mergeSettings: options?.mergeSettings ?? true,
        preserveAiSettings: options?.preserveAiSettings ?? true,
        onSuccess: (data) => {
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['chatbot-builder', chatbotId] });
          queryClient.invalidateQueries({ queryKey: ['chatbot', chatbotId] });
          
          setState(prev => ({
            ...prev,
            isSaving: false,
            lastSaved: new Date(),
            error: null
          }));

          if (onSuccess) onSuccess();
        },
        onError: (error) => {
          setState(prev => ({ ...prev, isSaving: false, error }));
          if (onError) onError(error);
        }
      });

      if (!result.success) {
        toast({
          variant: 'destructive',
          title: 'Error saving changes',
          description: result.error?.message || 'Failed to save chatbot changes'
        });
      } else {
        toast({
          title: 'Changes saved',
          description: 'Your changes have been saved successfully.'
        });
      }

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState(prev => ({ ...prev, isSaving: false, error: err }));
      
      toast({
        variant: 'destructive',
        title: 'Error saving changes',
        description: err.message
      });

      if (onError) onError(err);
      return { success: false, error: err };
    }
  }, [chatbotId, queryClient, toast, onSuccess, onError]);

  /**
   * Save only settings
   */
  const saveSettings = useCallback(async (
    settings: Partial<any>,
    options?: { mergeSettings?: boolean }
  ) => {
    return saveChatbotData({ settings }, { mergeSettings: options?.mergeSettings ?? true });
  }, [saveChatbotData]);

  /**
   * Save basic info (name, description, etc.)
   */
  const saveBasicInfo = useCallback(async (
    basicInfo: {
      name?: string;
      description?: string;
      title?: string;
      welcome_message?: string;
      completion_message?: string;
      active?: boolean;
    }
  ) => {
    return saveChatbotData(basicInfo, { mergeSettings: false });
  }, [saveChatbotData]);

  /**
   * Save theme
   */
  const saveTheme = useCallback(async (theme: Partial<ChatTheme>) => {
    if (!chatbotId) {
      const error = new Error('Chatbot ID is required');
      setState(prev => ({ ...prev, error }));
      return { success: false, error };
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      // Use centralized mutations service
      const data = await upsertTheme(chatbotId, theme);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot-builder', chatbotId] });
      queryClient.invalidateQueries({ queryKey: ['theme', chatbotId] });

      setState(prev => ({
        ...prev,
        isSaving: false,
        lastSaved: new Date(),
        error: null
      }));

      toast({
        title: 'Theme saved',
        description: 'Your theme has been saved successfully.'
      });

      if (onSuccess) onSuccess();

      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState(prev => ({ ...prev, isSaving: false, error: err }));

      toast({
        variant: 'destructive',
        title: 'Error saving theme',
        description: err.message
      });

      if (onError) onError(err);
      return { success: false, error: err };
    }
  }, [chatbotId, queryClient, toast, onSuccess, onError]);

  return {
    saveChatbotData,
    saveSettings,
    saveBasicInfo,
    saveTheme,
    isSaving: state.isSaving,
    lastSaved: state.lastSaved,
    error: state.error
  };
}

